import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import api from "../utils/api";

/* ── Styles ──────────────────────────────────────────────────────────────────── */
const CHAT_STYLES = `
  .chat-fab {
    position: fixed;
    bottom: 88px;
    right: 22px;
    z-index: 9000;
    width: 52px; height: 52px;
    border-radius: 50%;
    background: linear-gradient(135deg, #4f8ef7, #7c3aed);
    border: none; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    box-shadow: 0 4px 24px rgba(79,142,247,0.45), 0 1px 4px rgba(0,0,0,0.3);
    transition: transform .2s cubic-bezier(.16,1,.3,1), box-shadow .2s;
    color: #fff;
  }
  .chat-fab:hover {
    transform: scale(1.08) translateY(-2px);
    box-shadow: 0 8px 32px rgba(79,142,247,0.55), 0 2px 8px rgba(0,0,0,0.3);
  }
  .chat-fab-badge {
    position: absolute; top: -3px; right: -3px;
    min-width: 18px; height: 18px; border-radius: 50%;
    background: #ef4444; color: #fff;
    font-size: .62rem; font-weight: 800;
    display: flex; align-items: center; justify-content: center;
    border: 2px solid #08080f; padding: 0 3px;
    animation: chat-bounce-in .3s cubic-bezier(.16,1,.3,1);
  }
  @keyframes chat-bounce-in {
    from { transform: scale(0); opacity: 0; }
    to   { transform: scale(1); opacity: 1; }
  }

  /* Panel is always portaled to body, z-index above everything */
  .chat-panel {
    position: fixed;
    z-index: 9100;
    width: 360px;
    max-height: 520px;
    display: flex;
    flex-direction: column;
    background: #0e0e1a;
    border: 1px solid rgba(79,142,247,0.22);
    border-radius: 20px;
    overflow: hidden;
    box-shadow: 0 24px 64px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.04);
    animation: chat-panel-in .26s cubic-bezier(.16,1,.3,1);
  }
  @keyframes chat-panel-in {
    from { opacity: 0; transform: translateY(12px) scale(.97); }
    to   { opacity: 1; transform: translateY(0)   scale(1);    }
  }
  @media (max-width: 500px) {
    .chat-panel { width: calc(100vw - 24px); }
    .chat-fab   { bottom: 16px; right: 16px; }
  }

  .chat-header {
    display: flex; align-items: center; gap: 10px;
    padding: 13px 15px;
    background: linear-gradient(135deg, rgba(79,142,247,.11), rgba(124,58,237,.11));
    border-bottom: 1px solid rgba(255,255,255,.06);
    flex-shrink: 0;
  }
  .chat-header-avatar {
    width: 36px; height: 36px; border-radius: 50%;
    background: linear-gradient(135deg, #4f8ef7, #7c3aed);
    display: flex; align-items: center; justify-content: center;
    font-size: .74rem; font-weight: 800; color: #fff; flex-shrink: 0;
    position: relative;
  }
  .chat-online-dot {
    position: absolute; bottom: 0; right: 0;
    width: 10px; height: 10px; border-radius: 50%;
    background: #22c55e; border: 2px solid #0e0e1a;
    animation: chat-pulse-online 2.2s infinite;
  }
  @keyframes chat-pulse-online {
    0%,100% { box-shadow: 0 0 0 0 rgba(34,197,94,.55); }
    50%      { box-shadow: 0 0 0 5px rgba(34,197,94,0); }
  }
  .chat-header-info { flex: 1; min-width: 0; }
  .chat-header-name {
    font-size: .875rem; font-weight: 700; color: #ededf5;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .chat-header-status {
    font-size: .7rem; color: #22c55e; font-weight: 600;
    display: flex; align-items: center; gap: 4px; margin-top: 1px;
  }
  .chat-close-btn {
    width: 28px; height: 28px; border-radius: 8px;
    background: rgba(255,255,255,.06); border: none; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    color: #8888aa; transition: background .13s, color .13s; flex-shrink: 0;
  }
  .chat-close-btn:hover { background: rgba(255,255,255,.11); color: #ededf5; }

  .chat-messages {
    flex: 1; overflow-y: auto;
    padding: 14px 13px 6px;
    display: flex; flex-direction: column; gap: 8px;
  }
  .chat-messages::-webkit-scrollbar { width: 3px; }
  .chat-messages::-webkit-scrollbar-thumb { background: rgba(255,255,255,.1); border-radius: 2px; }

  .chat-empty-state {
    flex: 1; display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    gap: 10px; padding: 2rem 1rem; text-align: center;
  }
  .chat-empty-icon {
    width: 50px; height: 50px; border-radius: 14px;
    background: rgba(79,142,247,.08); border: 1px solid rgba(79,142,247,.15);
    display: flex; align-items: center; justify-content: center; color: #4f8ef7;
  }
  .chat-empty-state p { font-size: .8rem; line-height: 1.6; color: #8888aa; max-width: 210px; margin: 0; }

  .chat-date-sep {
    display: flex; align-items: center; gap: 8px;
    font-size: .66rem; color: #44445a; font-weight: 700;
    letter-spacing: .07em; text-transform: uppercase; flex-shrink: 0;
  }
  .chat-date-sep::before,.chat-date-sep::after {
    content: ''; flex: 1; height: 1px; background: rgba(255,255,255,.06);
  }

  .chat-msg { display: flex; flex-direction: column; max-width: 82%; }
  .chat-msg.me   { align-self: flex-end; align-items: flex-end; }
  .chat-msg.them { align-self: flex-start; align-items: flex-start; }
  .chat-bubble {
    padding: 9px 13px; border-radius: 16px;
    font-size: .83rem; line-height: 1.55;
    word-break: break-word; white-space: pre-wrap;
  }
  .chat-msg.me   .chat-bubble { background: linear-gradient(135deg,#4f8ef7,#6366f1); color:#fff; border-bottom-right-radius:5px; }
  .chat-msg.them .chat-bubble { background:rgba(255,255,255,.07); border:1px solid rgba(255,255,255,.08); color:#ededf5; border-bottom-left-radius:5px; }
  .chat-msg.pending { opacity:.7; }
  .chat-typing-bubble { padding: 4px 13px; }
  .chat-typing-bubble .chat-typing-dots span { background:#8888aa; }
  .chat-msg-time   { font-size:.63rem; color:#44445a; margin-top:3px; padding:0 3px; }
  .chat-sender-lbl { font-size:.63rem; font-weight:700; color:#8888aa; margin-bottom:3px; padding:0 3px; }

  .chat-typing-dots { display:flex; align-items:center; gap:4px; padding:10px 0; align-self:flex-start; }
  .chat-typing-dots span {
    width:7px; height:7px; border-radius:50%;
    background:#4f8ef7; opacity:.45;
    animation: chat-dot 1.4s infinite;
  }
  .chat-typing-dots span:nth-child(2){animation-delay:.2s;}
  .chat-typing-dots span:nth-child(3){animation-delay:.4s;}
  @keyframes chat-dot {
    0%,60%,100%{transform:translateY(0);opacity:.45;}
    30%{transform:translateY(-5px);opacity:1;}
  }

  .chat-input-row {
    display: flex; align-items: flex-end; gap: 8px;
    padding: 10px 11px 12px;
    border-top: 1px solid rgba(255,255,255,.06);
    background: rgba(0,0,0,.22); flex-shrink: 0;
  }
  .chat-textarea {
    flex: 1; resize: none;
    background: rgba(255,255,255,.06); border: 1px solid rgba(255,255,255,.1);
    border-radius: 12px; padding: 9px 12px;
    font-family: inherit; font-size: .83rem; color: #ededf5; outline: none;
    min-height: 38px; max-height: 100px; line-height: 1.5;
    transition: border-color .14s;
  }
  .chat-textarea::placeholder { color: #44445a; }
  .chat-textarea:focus { border-color: rgba(79,142,247,.5); }
  .chat-send-btn {
    width: 36px; height: 36px; flex-shrink: 0; border-radius: 10px;
    background: linear-gradient(135deg, #4f8ef7, #6366f1);
    border: none; cursor: pointer;
    display: flex; align-items: center; justify-content: center; color: #fff;
    transition: transform .14s, box-shadow .14s, opacity .14s;
  }
  .chat-send-btn:hover:not(:disabled) { transform:scale(1.07); box-shadow:0 4px 14px rgba(79,142,247,.45); }
  .chat-send-btn:disabled { opacity:.3; cursor:default; }

  /* ── Navbar trigger button ── */
  .chat-nav-btn {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 6px 13px; border-radius: 9px;
    background: rgba(79,142,247,.1); border: 1px solid rgba(79,142,247,.28);
    color: #4f8ef7; font-size: .875rem; font-weight: 600;
    cursor: pointer; font-family: inherit;
    transition: background .13s, border-color .13s, transform .13s;
    white-space: nowrap; position: relative;
  }
  .chat-nav-btn:hover {
    background: rgba(79,142,247,.18);
    border-color: rgba(79,142,247,.5);
    transform: translateY(-1px);
  }
  .chat-nav-badge {
    min-width: 17px; height: 17px; border-radius: 100px; padding: 0 4px;
    background: #ef4444; color: #fff; font-size: .6rem; font-weight: 800;
    display: inline-flex; align-items: center; justify-content: center; line-height: 1;
  }
`;

let stylesInjected = false;
function injectStyles() {
  if (stylesInjected || document.getElementById("ql-chat-styles")) {
    stylesInjected = true;
    return;
  }
  const s = document.createElement("style");
  s.id = "ql-chat-styles";
  s.textContent = CHAT_STYLES;
  document.head.appendChild(s);
  stylesInjected = true;
}

function fmt(dateStr) {
  return new Date(dateStr).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
}
function fmtDate(dateStr) {
  const d = new Date(dateStr),
    now = new Date();
  const yest = new Date(now);
  yest.setDate(yest.getDate() - 1);
  if (d.toDateString() === now.toDateString()) return "Today";
  if (d.toDateString() === yest.toDateString()) return "Yesterday";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/* ── Panel content (always portaled to body) ─────────────────────────────────── */
function ChatPanel({ onClose, anchorRect, asNavButton }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  // While > 0, the AI bot is "typing" its reply — show the dots until then.
  const [typingUntil, setTypingUntil] = useState(0);
  // Optimistic copy of the message being sent (the server generates the
  // bot's reply before /chat/send returns, which can take a few seconds).
  const [pending, setPending] = useState(null);
  const messagesRef = useRef(null);
  const pollRef = useRef(null);
  const panelRef = useRef(null);
  const didInitialScroll = useRef(false);
  const shouldScrollAfterSend = useRef(false);

  // Position panel: below the trigger button if nav-mode, else bottom-right corner
  const panelStyle =
    asNavButton && anchorRect
      ? {
          top: anchorRect.bottom + 10,
          right: Math.max(12, window.innerWidth - anchorRect.right),
          bottom: "auto",
        }
      : { bottom: 152, right: 22 };

  const load = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/chat/mine");
      setMessages(data);
    } catch {
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  const trackTyping = useCallback((botTypingUntil) => {
    if (!botTypingUntil) return;
    const until = new Date(botTypingUntil).getTime();
    if (until > Date.now()) setTypingUntil(until);
  }, []);

  useEffect(() => {
    load(false);
    // Reopened mid-reply? Pick the typing indicator back up.
    api
      .get("/interview/status")
      .then(({ data }) => trackTyping(data.botTypingUntil))
      .catch(() => {});
  }, [load, trackTyping]);

  useEffect(() => {
    pollRef.current = setInterval(() => load(true), 4000);
    return () => clearInterval(pollRef.current);
  }, [load]);

  // When the bot "finishes typing", clear the dots and pull its message.
  useEffect(() => {
    if (!typingUntil) return;
    const t = setTimeout(
      () => {
        setTypingUntil(0);
        shouldScrollAfterSend.current = true;
        load(true);
      },
      Math.max(typingUntil - Date.now(), 0) + 500,
    );
    return () => clearTimeout(t);
  }, [typingUntil, load]);

  const botTyping = typingUntil > 0 || sending;

  // Keep the pending message and the typing dots in view.
  useEffect(() => {
    const el = messagesRef.current;
    if (el && (pending || botTyping)) el.scrollTop = el.scrollHeight;
  }, [pending, botTyping]);

  useEffect(() => {
    const messagesEl = messagesRef.current;
    if (!messagesEl || messages.length === 0) return;

    if (!didInitialScroll.current) {
      messagesEl.scrollTop = messagesEl.scrollHeight;
      didInitialScroll.current = true;
      return;
    }

    if (shouldScrollAfterSend.current) {
      shouldScrollAfterSend.current = false;
      messagesEl.scrollTop = messagesEl.scrollHeight;
    }
  }, [messages]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target) &&
        !e.target.closest(".chat-nav-btn") &&
        !e.target.closest(".chat-fab")
      ) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  const send = async () => {
    const t = text.trim();
    if (!t || sending) return;
    setSending(true);
    setText("");
    setPending({
      _id: `pending-${Date.now()}`,
      senderRole: "user",
      text: t,
      createdAt: new Date().toISOString(),
      // Lets the render tell whether a poll already returned the saved copy.
      knownIds: new Set(messages.map((m) => m._id)),
    });
    try {
      const { data } = await api.post("/chat/send", { text: t });
      shouldScrollAfterSend.current = true;
      setMessages((prev) =>
        prev.some((m) => m._id === data._id) ? prev : [...prev, data],
      );
      trackTyping(data.botTypingUntil);
    } catch {
      // Not saved — give the text back so the user can retry.
      setText(t);
    } finally {
      setPending(null);
      setSending(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const pendingSaved =
    pending &&
    messages.some(
      (m) =>
        m.senderRole === "user" &&
        m.text === pending.text &&
        !pending.knownIds.has(m._id),
    );
  const shown = pending && !pendingSaved ? [...messages, pending] : messages;

  // Group by date
  const grouped = shown.reduce((acc, msg) => {
    const d = fmtDate(msg.createdAt);
    if (!acc.length || acc[acc.length - 1].date !== d)
      acc.push({ date: d, msgs: [] });
    acc[acc.length - 1].msgs.push(msg);
    return acc;
  }, []);

  return createPortal(
    <div className="chat-panel" ref={panelRef} style={panelStyle}>
      {/* Header */}
      <div className="chat-header">
        <div className="chat-header-avatar">
          QL
          <div className="chat-online-dot" />
        </div>
        <div className="chat-header-info">
          <div className="chat-header-name">Quantum Team</div>
          <div className="chat-header-status">
            <svg width="7" height="7" viewBox="0 0 8 8" fill="#22c55e">
              <circle cx="4" cy="4" r="4" />
            </svg>
            Online · replies fast
          </div>
        </div>
        <button className="chat-close-btn" onClick={onClose}>
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      {/* Messages */}
      <div className="chat-messages" ref={messagesRef}>
        {loading && messages.length === 0 ? (
          <div className="chat-empty-state">
            <div className="chat-typing-dots">
              <span />
              <span />
              <span />
            </div>
          </div>
        ) : shown.length === 0 && !botTyping ? (
          <div className="chat-empty-state">
            <div className="chat-empty-icon">
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
            </div>
            <p>
              Send us a message — our team typically responds within a few
              hours.
            </p>
          </div>
        ) : (
          grouped.map((g) => (
            <div
              key={g.date}
              style={{ display: "flex", flexDirection: "column", gap: 8 }}
            >
              <div className="chat-date-sep">{g.date}</div>
              {g.msgs.map((msg) => {
                const isMe = msg.senderRole === "user";
                return (
                  <div
                    key={msg._id}
                    className={`chat-msg ${isMe ? "me" : "them"}${msg.knownIds ? " pending" : ""}`}
                  >
                    {!isMe && (
                      <div className="chat-sender-lbl">Quantum Team</div>
                    )}
                    <div className="chat-bubble">{msg.text}</div>
                    <div className="chat-msg-time">{fmt(msg.createdAt)}</div>
                  </div>
                );
              })}
            </div>
          ))
        )}
        {botTyping && (
          <div className="chat-msg them">
            <div className="chat-sender-lbl">Quantum Team</div>
            <div className="chat-bubble chat-typing-bubble">
              <div className="chat-typing-dots">
                <span />
                <span />
                <span />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="chat-input-row">
        <textarea
          className="chat-textarea"
          placeholder="Type a message…"
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            e.target.style.height = "auto";
            e.target.style.height = Math.min(e.target.scrollHeight, 100) + "px";
          }}
          onKeyDown={handleKey}
          rows={1}
          disabled={sending}
          autoFocus
        />
        <button
          className="chat-send-btn"
          onClick={send}
          disabled={!text.trim() || sending}
        >
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
          >
            <line x1="22" y1="2" x2="11" y2="13" />
            <polygon points="22 2 15 22 11 13 2 9 22 2" />
          </svg>
        </button>
      </div>
    </div>,
    document.body,
  );
}

/* ══ Exported ChatWidget ══════════════════════════════════════════════════════ */
export default function ChatWidget({ user, asNavButton = false }) {
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const btnRef = useRef(null);
  const [anchorRect, setAnchorRect] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    injectStyles();
  }, []);

  const fetchUnread = useCallback(async () => {
    try {
      const { data } = await api.get("/chat/unread-count");
      setUnread(data.count);
    } catch {}
  }, []);

  // Poll unread count when closed
  useEffect(() => {
    fetchUnread();
    pollRef.current = setInterval(() => {
      if (!open) fetchUnread();
    }, 5000);
    return () => clearInterval(pollRef.current);
  }, [open, fetchUnread]);

  const toggle = () => {
    if (!open && btnRef.current) {
      setAnchorRect(btnRef.current.getBoundingClientRect());
    }
    setOpen((o) => !o);
    if (!open) setUnread(0); // optimistically clear badge on open
  };

  return (
    <>
      {asNavButton ? (
        <button ref={btnRef} className="chat-nav-btn" onClick={toggle}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
          >
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
          Chat with Team
          {unread > 0 && (
            <span className="chat-nav-badge">{unread > 9 ? "9+" : unread}</span>
          )}
        </button>
      ) : (
        <button
          ref={btnRef}
          className="chat-fab"
          onClick={toggle}
          title="Chat with Quantum Team"
        >
          {open ? (
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          ) : (
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          )}
          {!open && unread > 0 && (
            <span className="chat-fab-badge">{unread > 9 ? "9+" : unread}</span>
          )}
        </button>
      )}

      {open && (
        <ChatPanel
          onClose={() => setOpen(false)}
          anchorRect={anchorRect}
          asNavButton={asNavButton}
        />
      )}
    </>
  );
}
